import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  ArrowLeft, Plus, Send, FileText, Check, X, Eye, Clock, Trash2, Search
} from 'lucide-react';
import { toast, Toaster } from '../ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700' },
  viewed: { label: 'Viewed', color: 'bg-yellow-100 text-yellow-700' },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-700' },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-700' },
  expired: { label: 'Expired', color: 'bg-gray-100 text-gray-500' },
};

const QuoteDashboard = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState('');

  const [form, setForm] = useState({
    lead_name: '', lead_email: '', lead_company: '',
    selectedServices: [], customItems: [], discount_percent: 0, notes: '', valid_days: 30,
  });

  const fetchQuotes = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/quotes`);
      if (res.ok) setQuotes((await res.json()).quotes || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/quotes/services`);
      if (res.ok) setServices((await res.json()).services || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchQuotes(); fetchServices(); }, [fetchQuotes, fetchServices]);

  const toggleService = (id) => {
    setForm(f => ({
      ...f,
      selectedServices: f.selectedServices.includes(id)
        ? f.selectedServices.filter(s => s !== id)
        : [...f.selectedServices, id]
    }));
  };

  const addCustomItem = () => {
    setForm(f => ({ ...f, customItems: [...f.customItems, { name: '', description: '', amount: 0 }] }));
  };

  const updateCustomItem = (i, key, val) => {
    setForm(f => {
      const items = [...f.customItems];
      items[i] = { ...items[i], [key]: key === 'amount' ? Number(val) || 0 : val };
      return { ...f, customItems: items };
    });
  };

  const removeCustomItem = (i) => {
    setForm(f => ({ ...f, customItems: f.customItems.filter((_, idx) => idx !== i) }));
  };

  const getSubtotal = () => {
    const svcTotal = form.selectedServices.reduce((sum, id) => {
      const svc = services.find(s => s.id === id);
      return sum + (svc ? svc.monthly : 0);
    }, 0);
    const customTotal = form.customItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    return svcTotal + customTotal;
  };

  const createQuote = async () => {
    if (!form.lead_name || !form.lead_email || form.selectedServices.length === 0) {
      toast.error('Fill in client details and select at least one service');
      return;
    }
    const items = [
      ...form.selectedServices.map(id => {
        const svc = services.find(s => s.id === id);
        return { service_id: id, name: svc.name, description: svc.description, amount: svc.monthly, is_custom: false };
      }),
      ...form.customItems.filter(i => i.name && i.amount > 0).map(i => ({
        service_id: 'custom', name: i.name, description: i.description, amount: i.amount, is_custom: true
      }))
    ];

    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/quotes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, items }),
      });
      if (res.ok) {
        toast.success('Quote created!');
        setShowCreate(false);
        setForm({ lead_name: '', lead_email: '', lead_company: '', selectedServices: [], customItems: [], discount_percent: 0, notes: '', valid_days: 30 });
        fetchQuotes();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create quote');
      }
    } catch { toast.error('Network error'); }
  };

  const sendQuote = async (quoteNumber) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/quotes/${quoteNumber}/send`, { method: 'POST' });
      if (res.ok) { toast.success('Quote sent to client!'); fetchQuotes(); }
      else toast.error('Failed to send');
    } catch { toast.error('Network error'); }
  };

  const filtered = quotes.filter(q =>
    !filter || q.leadName?.toLowerCase().includes(filter.toLowerCase()) ||
    q.quoteNumber?.toLowerCase().includes(filter.toLowerCase()) ||
    q.leadEmail?.toLowerCase().includes(filter.toLowerCase())
  );

  const stats = {
    total: quotes.length,
    sent: quotes.filter(q => q.status === 'sent').length,
    viewed: quotes.filter(q => q.status === 'viewed').length,
    accepted: quotes.filter(q => q.status === 'accepted').length,
  };

  const subtotal = getSubtotal();
  const discount = Math.round(subtotal * form.discount_percent / 100);
  const tax = Math.round((subtotal - discount) * 0.18);
  const total = subtotal - discount + tax;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="quote-dashboard">
      <Toaster />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')} data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Quote Generator</h1>
              <p className="text-sm text-gray-500">Create and send service proposals</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-quote-btn">
            <Plus className="w-4 h-4 mr-2" /> New Quote
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total', value: stats.total, icon: FileText },
            { label: 'Sent', value: stats.sent, icon: Send },
            { label: 'Viewed', value: stats.viewed, icon: Eye },
            { label: 'Accepted', value: stats.accepted, icon: Check },
          ].map((s, i) => (
            <Card key={q.token || `quote-${i}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Create Quote Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-10 overflow-y-auto" data-testid="create-quote-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mb-10">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-xl font-bold">Create New Quote</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}><X className="w-4 h-4" /></Button>
              </div>

              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                {/* Client Info */}
                <div>
                  <h3 className="font-semibold text-sm text-gray-700 mb-3">Client Details</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <Input placeholder="Client name *" value={form.lead_name} onChange={e => setForm(f => ({ ...f, lead_name: e.target.value }))} data-testid="quote-client-name" />
                    <Input placeholder="Email *" type="email" value={form.lead_email} onChange={e => setForm(f => ({ ...f, lead_email: e.target.value }))} data-testid="quote-client-email" />
                    <Input placeholder="Company" value={form.lead_company} onChange={e => setForm(f => ({ ...f, lead_company: e.target.value }))} />
                  </div>
                </div>

                {/* Service Selection */}
                <div>
                  <h3 className="font-semibold text-sm text-gray-700 mb-3">Select Services</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {services.map(svc => (
                      <button key={svc.id} data-testid={`service-${svc.id}`}
                        onClick={() => toggleService(svc.id)}
                        className={`text-left p-3 rounded-lg border-2 transition-all ${
                          form.selectedServices.includes(svc.id) ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                        }`}>
                        <div className="flex justify-between items-start">
                          <span className="font-medium text-sm">{svc.name}</span>
                          <span className="text-sm font-bold text-emerald-600">₹{svc.monthly.toLocaleString()}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{svc.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Items */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-sm text-gray-700">Custom Line Items</h3>
                    <Button variant="outline" size="sm" onClick={addCustomItem}><Plus className="w-3 h-3 mr-1" /> Add</Button>
                  </div>
                  {form.customItems.map((item, i) => (
                    <div key={item.description || `item-${i}`} className="flex gap-2 mb-2">
                      <Input placeholder="Service name" className="flex-1" value={item.name} onChange={e => updateCustomItem(i, 'name', e.target.value)} />
                      <Input placeholder="Description" className="flex-1" value={item.description} onChange={e => updateCustomItem(i, 'description', e.target.value)} />
                      <Input type="number" placeholder="Amount" className="w-28" value={item.amount || ''} onChange={e => updateCustomItem(i, 'amount', e.target.value)} />
                      <Button variant="ghost" size="sm" onClick={() => removeCustomItem(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  ))}
                </div>

                {/* Discount & Notes */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Discount %</label>
                    <Input type="number" value={form.discount_percent || ''} onChange={e => setForm(f => ({ ...f, discount_percent: Number(e.target.value) || 0 }))} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Valid for (days)</label>
                    <Input type="number" value={form.valid_days} onChange={e => setForm(f => ({ ...f, valid_days: Number(e.target.value) || 30 }))} />
                  </div>
                </div>
                <Textarea placeholder="Internal notes (visible to client)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />

                {/* Price Summary */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span className="font-medium">₹{subtotal.toLocaleString()}/mo</span></div>
                  {discount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount ({form.discount_percent}%)</span><span>-₹{discount.toLocaleString()}</span></div>}
                  <div className="flex justify-between"><span>GST (18%)</span><span>₹{tax.toLocaleString()}</span></div>
                  <div className="flex justify-between border-t pt-2 text-base font-bold"><span>Total</span><span className="text-emerald-600">₹{total.toLocaleString()}/mo</span></div>
                </div>
              </div>

              <div className="p-6 border-t flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button onClick={createQuote} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-quote-btn">
                  <FileText className="w-4 h-4 mr-2" /> Create Quote
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Quotes List */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search quotes..." className="pl-9" value={filter} onChange={e => setFilter(e.target.value)} data-testid="quote-search" />
          </div>
        </div>

        <div className="space-y-3">
          {loading ? (
            <p className="text-center text-gray-500 py-12">Loading quotes...</p>
          ) : filtered.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No quotes yet. Create your first one!</p>
            </CardContent></Card>
          ) : filtered.map(q => {
            const st = STATUS_MAP[q.status] || STATUS_MAP.draft;
            return (
              <Card key={q.quoteNumber} className="hover:shadow-md transition-shadow" data-testid={`quote-row-${q.quoteNumber}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{q.quoteNumber}</span>
                        <Badge className={st.color}>{st.label}</Badge>
                      </div>
                      <p className="text-sm text-gray-600">{q.leadName} {q.leadCompany ? `— ${q.leadCompany}` : ''}</p>
                      <p className="text-xs text-gray-400">{q.leadEmail}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-emerald-600">₹{q.total?.toLocaleString()}/mo</p>
                      <p className="text-xs text-gray-400">{q.items?.length || 0} services</p>
                    </div>
                    <div className="flex gap-2">
                      {q.status === 'draft' && (
                        <Button size="sm" onClick={() => sendQuote(q.quoteNumber)} className="bg-blue-600 hover:bg-blue-700" data-testid={`send-${q.quoteNumber}`}>
                          <Send className="w-3.5 h-3.5 mr-1" /> Send
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => window.open(`/quote/${q.token}`, '_blank')} data-testid={`preview-${q.quoteNumber}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default QuoteDashboard;
